import { useEffect, useRef, useState, type Key, type ReactNode } from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';
import SearchResult from './SearchResult';
import { useEventListener } from 'usehooks-ts';
import { TextInput } from '../shared/input/Input';
import { XIcon } from '../shared/icons';

interface Props<T> {
    value?: string;
    onChange: (search: string) => void;
    onDebouncedChange?: (search: string) => void;
    list: T[];
    onResult?: (result: T | undefined) => void;
    status: string;
    id?: string;
    placeholder?: string;
    invalid?: boolean;
    getLabel: (result: T) => string;
    getKey?: (result: T) => Key;
    renderResult?: (result: T) => ReactNode;
    overWriteInput?: boolean;
}

// This component is base class for search boxes.
// It does not handle queries or decide what gets displayed.
export default function SearchBox<T>({
    value = '',
    onChange: setChange,
    onDebouncedChange,
    list,
    getLabel,
    getKey,
    onResult: setResult,
    renderResult,
    status,
    id,
    placeholder = 'Search...',
    invalid = false,
    overWriteInput = false,
}: Props<T>) {
    const [visible, setVisible] = useState(false); // State of the search results
    const timer = useRef<NodeJS.Timeout>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onDebouncedChange) return;

        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            onDebouncedChange(value);
        }, 500);

        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [onDebouncedChange, setChange, value]);

    useEventListener('click', (e) => {
        if (!(e.target instanceof Node)) return;

        if (!containerRef.current?.contains(e.target)) {
            setVisible(false);
        }
    });

    function handleChange(value: string) {
        setChange(value);
        setVisible(true);
    }

    // When the user clicks a result, set search state and pass the clicked result to parent
    function handleClick(r?: T) {
        setResult?.(r);
        setVisible(false);
        if (overWriteInput && r) setChange(getLabel(r));
    }

    function keyDown(event: React.KeyboardEvent) {
        if (event.key === 'Enter') {
            if (value === '') return;

            handleClick(list[0]);
            inputRef.current?.blur();
        }
    }

    return (
        <div className='grow relative' ref={containerRef}>
            <TextInput
                ref={inputRef}
                value={value}
                id={id}
                onKeyDown={keyDown}
                placeholder={placeholder}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => setVisible(true)}
                invalid={invalid}
                className={value !== '' ? 'pe-10' : undefined}
            />
            {value !== '' && (
                <button
                    type='button'
                    aria-label='Clear search'
                    className='absolute top-0 right-0 grid size-9 place-items-center text-theme-400 transition-colors hover:text-theme-text'
                    onClick={() => setChange('')}
                >
                    <XIcon size={20} />
                </button>
            )}
            <div
                className={
                    'absolute left-0 right-0 top-full mt-1 bg-theme-700 p-1 border border-theme-outline round:rounded-xl z-10 shadow-2xl overflow-hidden grid transition-all ' +
                    (visible ? 'opacity-100' : 'opacity-0 pointer-events-none')
                }
                style={{ gridTemplateRows: visible ? '1fr' : '0fr' }}
            >
                {status === 'pending' ? (
                    <LoadingSpinner />
                ) : status === 'error' || list.length === 0 ? (
                    <div className='px-2 py-1 min-h-0'>
                        <p>No results</p>
                    </div>
                ) : (
                    <ul className='min-h-0'>
                        {list.map((r) => (
                            <SearchResult onClick={() => handleClick(r)} key={getKey?.(r) ?? getLabel(r)}>
                                {renderResult ? renderResult(r) : <span>{getLabel(r)}</span>}
                            </SearchResult>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
